/**
 * SupportReportButton - the lifebuoy in the bottom-left corner of every app
 * (ROP-teams, Admin, Risk Management, SysOps). Clicking it takes the screen
 * picture first, in the background, then opens the dialog over the top.
 */
import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { LifeBuoy } from 'lucide-react';
import { SupportReportDialog, type SupportReportHostProps } from './SupportReportDialog';
import { captureScreen, type ScreenPicture } from './screenshot';

export function SupportReportButton(props: SupportReportHostProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [picture, setPicture] = useState<ScreenPicture | null | undefined>(undefined);

  const onOpen = (): void => {
    setPicture(undefined);
    // Start drawing before the dialog renders; the dialog is excluded from the picture regardless.
    const pending = captureScreen();
    setOpen(true);
    props.onOpenChange?.(true);
    void pending.then(setPicture);
  };
  const { onOpenChange } = props;
  const onClose = useCallback(() => { setOpen(false); onOpenChange?.(false); }, [onOpenChange]);

  // Bill 2026-10-16 (same fix as LWP 1.461.0): the lifebuoy must be reachable on EVERY screen, pop-up
  // and dialog. It used to render inside the app layout at z-index 61, under every modal (100-10000).
  // Button and report dialog now render on <body> (portal) above everything (z-index in SupportReport.css).
  if (typeof document === 'undefined') return <></>;
  return createPortal(
    <>
      <button type="button" className="support-fab" data-tour="report-problem" onClick={onOpen}
        title="Report a problem or suggest a change" aria-label="Report a problem or suggest a change">
        <LifeBuoy size={16} aria-hidden />
      </button>
      <SupportReportDialog {...props} open={open} onClose={onClose} picture={picture} />
    </>,
    document.body,
  );
}

export default SupportReportButton;
